// apps/provider/src/services/api/workoutPlans.js
// Workout plan CRUD, week management, schedule management, and composite
// operations (create from prescription, get complete plan with nested data).
//
// Methods: createWorkoutPlan, getWorkoutPlans, getWorkoutPlan, updateWorkoutPlan, deleteWorkoutPlan,
//          createWorkoutPlanWeek, getWorkoutPlanWeeks, updateWorkoutPlanWeek, deleteWorkoutPlanWeek,
//          createWorkoutPlanSchedule, getWorkoutPlanSchedules, updateWorkoutPlanSchedule, deleteWorkoutPlanSchedule,
//          getCompleteWorkoutPlan, createWorkoutPlanFromPrescription, getDaysFromFrequency
//
// Cross-domain calls (via composed `this`):
//   getCompleteWorkoutPlan → exercises.getExercisePrescription, rehabPrograms.getPatientExerciseAssignments
//   createWorkoutPlanFromPrescription → exercises.createExercisePrescription
//
// Response types: @prism-ehr/shared → WorkoutPlan, WorkoutPlanWeek, WorkoutPlanSchedule,
//                 CompleteWorkoutPlan, WorkoutPlanWeekWithSchedules,
//                 WorkoutPlanScheduleWithPrescription

/**
 * @returns {Object} Workout plan domain methods (bound to `this` of the composed apiService)
 */
export function createWorkoutPlansMethods() {
  return {

    // ============ Workout Plan CRUD ============

    /**
     * Create a new workout plan.
     *
     * Xano: POST /workout_plan
     * Xano staging file: 687_workout_plan_POST.xs
     * Xano production file: 335_workout_plan_POST.xs
     *
     * @param {Object} data - Plan data (name, description, plan_type, fitness_level, duration_weeks, etc.)
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlan>}
     */
    async createWorkoutPlan(data) {
      return this.request('/workout_plan', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get workout plans with optional filters.
     *
     * Xano: GET /workout_plan
     * Xano staging file: 686_workout_plan_GET.xs
     * Xano production file: 334_workout_plan_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.created_by]
     * @param {boolean} [filters.is_template]
     * @param {string} [filters.template_visibility]
     * @param {string} [filters.plan_type]
     * @param {number} [filters.templates_page]
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlan[]>}
     */
    async getWorkoutPlans(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.created_by) queryParams.append('created_by', filters.created_by);
      if (filters.is_template !== undefined) queryParams.append('is_template', filters.is_template);
      if (filters.template_visibility) queryParams.append('template_visibility', filters.template_visibility);
      if (filters.plan_type) queryParams.append('plan_type', filters.plan_type);
      if (filters.templates_page !== undefined) queryParams.append('templates_page', filters.templates_page);

      return this.request(`/workout_plan?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single workout plan by ID.
     *
     * Xano: GET /workout_plan/{id}
     * Xano staging file: 685_workout_plan_workout_paln_id_GET.xs
     * Xano production file: 337_workout_plan_workout_paln_id_GET.xs
     *
     * @param {number|string} id - Workout plan ID
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlan>}
     */
    async getWorkoutPlan(id) {
      return this.request(`/workout_plan/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update an existing workout plan.
     *
     * Xano: PATCH /workout_plan/{id}
     * Xano staging file: 688_workout_plan_workout_paln_id_PATCH.xs
     * Xano production file: 338_workout_plan_workout_paln_id_PATCH.xs
     *
     * @param {number|string} id - Workout plan ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlan>}
     */
    async updateWorkoutPlan(id, data) {
      return this.request(`/workout_plan/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a workout plan.
     *
     * Xano: DELETE /workout_plan/{id}
     * Xano staging file: 684_workout_plan_workout_paln_id_DELETE.xs
     * Xano production file: 336_workout_plan_workout_paln_id_DELETE.xs
     *
     * @param {number|string} id - Workout plan ID
     * @returns {Promise<void>}
     */
    async deleteWorkoutPlan(id) {
      return this.request(`/workout_plan/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Workout Plan Week CRUD ============

    /**
     * Create a new week within a workout plan.
     *
     * Xano: POST /workout_plan_week
     * Xano staging file: 692_workout_plan_week_POST.xs
     * Xano production file: 346_workout_plan_week_POST.xs
     *
     * @param {Object} data - Week data (workout_plan_id, week_number, notes)
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanWeek>}
     */
    async createWorkoutPlanWeek(data) {
      return this.request('/workout_plan_week', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get all weeks for a workout plan.
     *
     * Xano: GET /workout_plan_week
     * Xano staging file: 691_workout_plan_week_GET.xs
     * Xano production file: 345_workout_plan_week_GET.xs
     *
     * @param {number|string} workoutPlanId - Workout plan ID
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanWeek[]>}
     */
    async getWorkoutPlanWeeks(workoutPlanId) {
      return this.request(`/workout_plan_week?workout_plan_id=${workoutPlanId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a workout plan week.
     *
     * Xano: PATCH /workout_plan_week/{id}
     * Xano staging file: 693_workout_plan_week_workout_plan_week_id_PATCH.xs
     * Xano production file: 349_workout_plan_week_workout_plan_week_id_PATCH.xs
     *
     * @param {number|string} id - Week ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanWeek>}
     */
    async updateWorkoutPlanWeek(id, data) {
      return this.request(`/workout_plan_week/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a workout plan week.
     *
     * Xano: DELETE /workout_plan_week/{id}
     * Xano staging file: 689_workout_plan_week_workout_plan_week_id_DELETE.xs
     * Xano production file: 347_workout_plan_week_workout_plan_week_id_DELETE.xs
     *
     * @param {number|string} id - Week ID
     * @returns {Promise<void>}
     */
    async deleteWorkoutPlanWeek(id) {
      return this.request(`/workout_plan_week/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Workout Plan Schedule CRUD ============

    /**
     * Create a new schedule entry (assigns a prescription to a day within a week).
     *
     * Xano: POST /workout_plan_schedule
     * Xano staging file: 697_workout_plan_schedule_POST.xs
     * Xano production file: 341_workout_plan_schedule_POST.xs
     *
     * @param {Object} data - Schedule data (workout_plan_week_id, exercise_prescription_id, day_of_week, sequence, is_optional)
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanSchedule>}
     */
    async createWorkoutPlanSchedule(data) {
      return this.request('/workout_plan_schedule', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get all schedule entries for a workout plan week.
     *
     * Xano: GET /workout_plan_schedule
     * Xano staging file: 696_workout_plan_schedule_GET.xs
     * Xano production file: 340_workout_plan_schedule_GET.xs
     *
     * @param {number|string} workoutPlanWeekId - Week ID
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanSchedule[]>}
     */
    async getWorkoutPlanSchedules(workoutPlanWeekId) {
      return this.request(`/workout_plan_schedule?workout_plan_week_id=${workoutPlanWeekId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a workout plan schedule entry.
     *
     * Xano: PATCH /workout_plan_schedule/{id}
     * Xano staging file: 698_workout_plan_schedule_workout_plan_schedule_id_PATCH.xs
     * Xano production file: 344_workout_plan_schedule_workout_plan_schedule_id_PATCH.xs
     *
     * @param {number|string} id - Schedule entry ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').WorkoutPlanSchedule>}
     */
    async updateWorkoutPlanSchedule(id, data) {
      return this.request(`/workout_plan_schedule/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a workout plan schedule entry.
     *
     * Xano: DELETE /workout_plan_schedule/{id}
     * Xano staging file: 694_workout_plan_schedule_workout_plan_schedule_id_DELETE.xs
     * Xano production file: 342_workout_plan_schedule_workout_plan_schedule_id_DELETE.xs
     *
     * @param {number|string} id - Schedule entry ID
     * @returns {Promise<void>}
     */
    async deleteWorkoutPlanSchedule(id) {
      return this.request(`/workout_plan_schedule/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Composite / Helper Methods ============

    /**
     * Get a complete workout plan with nested weeks, schedules, prescriptions,
     * and active assignment count.
     *
     * Cross-domain: calls exercises.getExercisePrescription and
     * rehabPrograms.getPatientExerciseAssignments via composed `this`.
     *
     * @param {number|string} id - Workout plan ID
     * @returns {Promise<import('@prism-ehr/shared').CompleteWorkoutPlan>}
     */
    async getCompleteWorkoutPlan(id) {
      try {
        // 1. Get the base plan
        const plan = await this.getWorkoutPlan(id);

        // 2. Get all weeks for this plan
        const weeksResponse = await this.getWorkoutPlanWeeks(id);
        const weeks = weeksResponse || [];

        // 3. Get schedules for each week and prescriptions
        const weeksWithSchedules = await Promise.all(
          weeks.map(async (week) => {
            const schedulesResponse = await this.getWorkoutPlanSchedules(week.id);
            const schedules = schedulesResponse || [];

            // 4. Get prescription details for each schedule
            const schedulesWithPrescriptions = await Promise.all(
              schedules.map(async (schedule) => {
                try {
                  const prescription = await this.getExercisePrescription(schedule.exercise_prescription_id);
                  return {
                    ...schedule,
                    prescription
                  };
                } catch (error) {
                  console.error('Failed to fetch prescription:', error);
                  return schedule;
                }
              })
            );

            return {
              ...week,
              schedules: schedulesWithPrescriptions
            };
          })
        );

        // 5. Get assignment count
        const assignmentsResponse = await this.getPatientExerciseAssignments({
          workout_plan_id: id,
          status: 'active'
        });

        return {
          ...plan,
          weeks: weeksWithSchedules.sort((a, b) => a.week_number - b.week_number),
          assignment_count: assignmentsResponse.length || 0
        };
      } catch (error) {
        console.error('Failed to get complete workout plan:', error);
        throw error;
      }
    },

    /**
     * Create a full workout plan from prescription data: creates the exercise
     * prescription, workout plan, weeks, and schedules in sequence.
     *
     * Cross-domain: calls exercises.createExercisePrescription via composed `this`.
     *
     * @param {Object} prescriptionData - Exercise prescription data
     * @param {Object} planData - Workout plan metadata (name, description, planType, fitnessLevel, duration_weeks, etc.)
     * @param {Object|null} [workoutSchedule=null] - Optional per-week day schedule override (keyed by week_number → array of day integers)
     * @returns {Promise<{plan: import('@prism-ehr/shared').WorkoutPlan, weeks: import('@prism-ehr/shared').WorkoutPlanWeek[], prescription: import('@prism-ehr/shared').ExercisePrescription}>}
     */
    async createWorkoutPlanFromPrescription(prescriptionData, planData, workoutSchedule = null) {
      try {
        // 1. Create the exercise prescription
        const prescription = await this.createExercisePrescription(prescriptionData);

        // 2. Create the workout plan
        const plan = await this.createWorkoutPlan({
          name: planData.name,
          description: planData.description || prescriptionData.description,
          plan_type: planData.planType || 'general',
          fitness_level: planData.fitnessLevel || 'intermediate',
          duration_weeks: planData.duration_weeks || 4,
          created_by: prescriptionData.provider_id,
          organization_id: prescriptionData.organization_id,
          is_template: planData.is_template || false,
          template_visibility: planData.template_visibility || 'private'
        });

        // 3. Create weeks
        const weeks = [];
        for (let i = 1; i <= planData.duration_weeks; i++) {
          const week = await this.createWorkoutPlanWeek({
            workout_plan_id: plan.id,
            week_number: i,
            notes: ''
          });
          weeks.push(week);
        }

        // 4. Assign workout to days based on provided schedule or defaults
        if (workoutSchedule) {
          // Use the custom schedule from UI
          for (const week of weeks) {
            const weekSchedule = workoutSchedule[week.week_number] || [];
            let sequence = 1;

            for (const dayOfWeek of weekSchedule) {
              await this.createWorkoutPlanSchedule({
                workout_plan_week_id: week.id,
                exercise_prescription_id: prescription.id,
                day_of_week: dayOfWeek,
                is_optional: false,
                sequence: sequence++
              });
            }
          }
        } else {
          // Fall back to default frequency-based assignment
          const daysPerWeek = this.getDaysFromFrequency(
            prescriptionData.schedule?.workout_schedule?.frequency || '3x_week'
          );

          for (const week of weeks) {
            let sequence = 1;
            for (const dayOfWeek of daysPerWeek) {
              await this.createWorkoutPlanSchedule({
                workout_plan_week_id: week.id,
                exercise_prescription_id: prescription.id,
                day_of_week: dayOfWeek,
                is_optional: false,
                sequence: sequence++
              });
            }
          }
        }

        return { plan, weeks, prescription };
      } catch (error) {
        console.error('Failed to create workout plan from prescription:', error);
        throw error;
      }
    },

    /**
     * Helper to convert a frequency string to an array of day-of-week integers (1=Mon … 7=Sun).
     *
     * @param {string} frequency - e.g. 'daily', '3x_week', '5x_week'
     * @returns {number[]} Array of day integers
     */
    getDaysFromFrequency(frequency) {
      const dayMappings = {
        'daily': [1, 2, 3, 4, 5, 6, 7],
        '2x_week': [2, 5], // Tuesday, Friday
        '3x_week': [1, 3, 5], // Monday, Wednesday, Friday
        '4x_week': [1, 2, 4, 5], // Mon, Tue, Thu, Fri
        '5x_week': [1, 2, 3, 4, 5], // Weekdays
        '6x_week': [1, 2, 3, 4, 5, 6], // Mon-Sat
        'custom': [] // Will need to handle separately
      };

      return dayMappings[frequency] || dayMappings['3x_week'];
    },

  };
}
